/**
 * One negotiation at a time, a queue for the rest, and a watchdog over both.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * An `RTCPeerConnection` carries **one** negotiation at a time, and every
 * publish and every pull is one. The SFU answers a second request made
 * mid-exchange with "The current signaling state is expecting a remote answer:
 * renegotiation is expected before pulling or pushing tracks." Pulling a roster
 * the moment it arrives — before our own publish has even been answered — is
 * exactly that, and it is the reason this is a queue and not a loop over the
 * roster. The desktop learned it the same way (`media/callcontroller.h`).
 *
 * ── Why a task rather than a flag ───────────────────────────────────────────
 * The Qt controller sets a `m_negotiating` flag and clears it in whichever
 * callback happens to end the exchange — which is `publish`'s answer for a
 * publish, but the *renegotiate* reply for a pull, one step further on than it
 * looks. Here an exchange is one `async` function, so "busy until the exchange
 * is finished" is just `await`, and the pull's tail cannot be released early by
 * somebody reading the shorter of the two paths.
 *
 * ── The watchdog ────────────────────────────────────────────────────────────
 * Every path that starts a negotiation has a matching end, but "every path"
 * only covers the ones that return. A promise that never settles would hold the
 * queue for the rest of the call and the queue behind it would stop draining
 * with nothing to show for it. A stalled queue is invisible, which is the
 * reason to watch it: after 15 seconds the queue gives up waiting and moves on.
 * The abandoned task is not cancelled — nothing here can cancel an SDP exchange
 * — it is simply no longer waited for, and its own caller still hears how it
 * ended.
 */

export const NEGOTIATION_TIMEOUT_MS = 15_000;

/** The queue was cleared before this task ran. Callers treat it as "the call
 *  ended underneath me", which is what it always means. */
export class NegotiationCancelled extends Error {
  constructor() {
    super('The call ended.');
    this.name = 'NegotiationCancelled';
  }
}

interface Entry<T> {
  label: string;
  task: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
}

export interface NegotiationQueueOptions {
  timeoutMs?: number;
  /** Told which negotiation stalled, so a session can log it. Nothing here
   *  fails the call over it: a stalled pull is one peer, not the room. */
  onStalled?: (label: string) => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export class NegotiationQueue {
  private readonly timeoutMs: number;
  private readonly onStalled: (label: string) => void;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  // `Entry<unknown>` because the queue holds differently-typed tasks and the
  // only place a task's type is known is `run`, where `T` is bound.
  private readonly queued: Array<Entry<unknown>> = [];
  private running: Entry<unknown> | null = null;

  constructor(options: NegotiationQueueOptions = {}) {
    this.timeoutMs = options.timeoutMs ?? NEGOTIATION_TIMEOUT_MS;
    this.onStalled = options.onStalled ?? (() => {});
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  get busy(): boolean {
    return this.running !== null;
  }

  get pending(): number {
    return this.queued.length;
  }

  run<T>(label: string, task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queued.push({
        label,
        task: task as () => Promise<unknown>,
        resolve: resolve as (value: unknown) => void,
        reject
      });
      this.drain();
    });
  }

  /** Drop everything still waiting. Hanging up, or a rejoin that makes every
   *  queued pull describe a session that no longer exists. */
  clear(): void {
    const dropped = this.queued.splice(0, this.queued.length);
    for (const entry of dropped) entry.reject(new NegotiationCancelled());
  }

  private drain(): void {
    if (this.running) return;
    const next = this.queued.shift();
    if (!next) return;

    this.running = next;
    const watchdog = this.setTimer(() => {
      if (this.running !== next) return;
      this.onStalled(next.label);
      this.running = null;
      this.drain();
    }, this.timeoutMs);

    void next
      .task()
      .then(next.resolve, next.reject)
      .then(() => {
        this.clearTimer(watchdog);
        if (this.running !== next) return; // the watchdog already moved on
        this.running = null;
        this.drain();
      });
  }
}
